"use client";

import { CheckCircle2, Circle, Hand, Sparkles } from "lucide-react";
import { BuildingCompanyProfileProgress } from "~/components/siteglue/siteglue-extended-knowledge-panel";
import type { SiteGlueConsultantState } from "~/lib/siteglue/consultant-onboarding";
import { createEmptyConsultantState } from "~/lib/siteglue/consultant-onboarding";
import {
  AI_EMPLOYEE_FEATURE_SECTIONS,
  AI_EMPLOYEE_PLANS,
  formatAiEmployeePlanHeaderLabel,
  type AiEmployeePlanId,
} from "~/lib/siteglue/ai-employee-plans";
import { defaultAvatarUrlForAgentName } from "~/lib/siteglue/ai-employee-role-catalog";
import {
  SITEGLUE_ONBOARDING_CHECKLIST,
  siteGlueChecklistProgressPercent,
} from "~/lib/siteglue/onboarding-checklist-progress";
import { cn } from "~/lib/utils";

type YourAiEmployeePreviewProps = {
  state: SiteGlueConsultantState | null;
  className?: string;
  isSignedIn?: boolean;
};

type SignatureChannel = {
  label: string;
  active: boolean;
  detail?: string;
};

function planIdFromState(state: SiteGlueConsultantState): AiEmployeePlanId {
  return state.selectedPlan ?? state.recommendedPlan ?? "intern";
}

function featureEnabled(planId: AiEmployeePlanId, featureId: string): boolean {
  for (const section of AI_EMPLOYEE_FEATURE_SECTIONS) {
    const feature = section.features.find((f) => f.id === featureId);
    if (!feature) continue;
    return feature.values[planId] === true;
  }
  return false;
}

function signatureChannels(
  state: SiteGlueConsultantState,
  planId: AiEmployeePlanId,
): SignatureChannel[] {
  const website = state.websiteUrl?.trim();
  return [
    {
      label: "Direct Line",
      active: featureEnabled(planId, "phone_calls"),
    },
    {
      label: "Email",
      active: featureEnabled(planId, "email_channel"),
    },
    {
      label: "Live Chat",
      active:
        featureEnabled(planId, "website_chat") ||
        state.recommendedChannels.some((c) => /chat/i.test(c)),
    },
    {
      label: "Voice (WebRTC)",
      active: featureEnabled(planId, "web_voice"),
    },
    {
      label: "Video Calls",
      active: featureEnabled(planId, "video_calls"),
    },
    {
      label: "Website",
      active: Boolean(website),
      detail: website || undefined,
    },
  ];
}

function recommendedResponsibilities(
  state: SiteGlueConsultantState,
  planId: AiEmployeePlanId,
): string[] {
  const fromAgent = state.recommendedAgents[0];
  const fromReport = state.report?.desiredOutcomePlans?.[0]?.recommendedActions ?? [];
  if (fromReport.length > 0) return fromReport.slice(0, 4);
  if (fromAgent?.businessNeed?.trim()) {
    return [
      fromAgent.businessNeed.trim(),
      fromAgent.expectedBenefit?.trim(),
      fromAgent.humanHandoff?.trim(),
    ].filter(Boolean) as string[];
  }

  const skills =
    AI_EMPLOYEE_FEATURE_SECTIONS.find((s) => s.id === "skills")?.features.filter(
      (f) => f.values[planId] === true,
    ) ?? [];
  if (skills.length > 0) return skills.slice(0, 4).map((s) => s.label);

  return [
    "Answer website questions 24/7",
    "Capture leads and appointment requests",
    "Hand off to your team with full context",
  ];
}

function RecommendedEmployeeCard({
  state,
  displayName,
  displayRole,
  avatarUrl,
}: {
  state: SiteGlueConsultantState;
  displayName: string;
  displayRole: string;
  avatarUrl: string;
}) {
  const planId = planIdFromState(state);
  const plan = AI_EMPLOYEE_PLANS.find((p) => p.id === planId);
  const businessName = state.businessName?.trim() || "Your organization";
  const channels = signatureChannels(state, planId);
  const responsibilities = recommendedResponsibilities(state, planId);
  const channelLabels =
    state.recommendedChannels.length > 0
      ? state.recommendedChannels
      : channels.filter((c) => c.active && c.label !== "Website").map((c) => c.label);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-[#9cbb5a]/30 bg-[#9cbb5a]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#6b8f2e]">
          <Hand className="h-3 w-3" aria-hidden />
          Human-in-the-Loop™
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#4f81bd]">
          Recommended AI Employee
        </p>

        <div className="mt-3 flex items-start gap-3 border-b border-slate-100 pb-4">
          {avatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={avatarUrl}
              alt={displayName}
              className="h-16 w-16 shrink-0 rounded-full object-cover ring-2 ring-slate-100"
            />
          ) : (
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-400">
              <Sparkles className="h-7 w-7" aria-hidden />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-lg font-bold tracking-tight text-slate-900">{displayName}</p>
            <p className="text-sm font-medium text-slate-700">{displayRole}</p>
            <p className="text-sm text-slate-500">{businessName}</p>
            <dl className="mt-3 space-y-1 text-xs leading-snug">
              {channels.map((ch) => (
                <div key={ch.label} className="flex flex-wrap gap-x-1.5">
                  <dt className="font-semibold text-slate-700">{ch.label}:</dt>
                  <dd
                    className={cn(
                      ch.active ? "font-medium text-emerald-600" : "text-slate-500",
                    )}
                  >
                    {ch.active ? ch.detail?.trim() || "Active" : "Disabled"}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <section>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-400">
              Recommended Responsibilities
            </p>
            <ul className="mt-2 space-y-1.5">
              {responsibilities.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#9cbb5a]"
                    aria-hidden
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-400">
              Recommended Channels
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {channelLabels.map((ch) => (
                <span
                  key={ch}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
                >
                  {ch}
                </span>
              ))}
            </div>
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-400">
              Recommended Pricing Plans
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="rounded-full border border-slate-900 bg-slate-900 px-2.5 py-1 text-xs font-medium text-white">
                {formatAiEmployeePlanHeaderLabel(planId)}
                {planId === "intern" ? " · recommended" : ""}
              </span>
              {planId === "intern" ? (
                <>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                    Associate · $99/mo
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                    Professional · $199/mo
                  </span>
                </>
              ) : plan ? (
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                  {plan.tagline}
                </span>
              ) : null}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/** Progress tab — onboarding checklist + recommended AI Employee summary. */
export function YourAiEmployeePreview({
  state,
  className,
  isSignedIn = false,
}: YourAiEmployeePreviewProps) {
  const effectiveState = state ?? createEmptyConsultantState({ step: "website" });
  const checklistCtx = { isSignedIn };

  const primary = effectiveState.recommendedAgents[0];
  const namedEmployee =
    effectiveState.employeeName &&
    !/^(to|the|a|an|name|agent|employee|him|her|them|it)$/i.test(effectiveState.employeeName)
      ? effectiveState.employeeName.trim()
      : "";
  const displayName = namedEmployee || "Samantha";
  const displayRole =
    effectiveState.report?.recommendedEmployee?.role?.trim() ||
    primary?.roleName ||
    "Online Support Specialist";
  const avatarUrl =
    effectiveState.employeeProfileImageUrl?.trim() ||
    effectiveState.report?.recommendedEmployee?.profileImageUrl?.trim() ||
    (namedEmployee
      ? defaultAvatarUrlForAgentName(namedEmployee)
      : "/images/profiles/Samantha.webp");

  const internPreviewReady =
    Boolean(namedEmployee) &&
    (effectiveState.step === "complete" ||
      Boolean(effectiveState.companyProfileMarkdown?.trim()) ||
      ((effectiveState.budgetConfirmed ||
        effectiveState.selectedPlan === "intern" ||
        effectiveState.recommendedPlan === "intern") &&
        Boolean(namedEmployee)));

  if (
    (effectiveState.step === "analyzing" || effectiveState.step === "building_knowledge") &&
    !internPreviewReady &&
    !namedEmployee
  ) {
    return <BuildingCompanyProfileProgress className={className} />;
  }

  const progressPercent = siteGlueChecklistProgressPercent(effectiveState, checklistCtx);

  return (
    <div className={cn("h-full overflow-y-auto p-4 sm:p-5", className)}>
      <div className="grid gap-5 lg:grid-cols-2 lg:items-start lg:gap-6">
        <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
              Onboarding Progress
            </p>
            <p className="mt-1 text-sm text-slate-600">
              Progress: <strong className="text-slate-900">{progressPercent}%</strong>
            </p>
          </div>
          <ul className="space-y-2.5">
            {SITEGLUE_ONBOARDING_CHECKLIST.map((item) => {
              const done = item.done(effectiveState, checklistCtx);
              return (
                <li key={item.id} className="flex items-center gap-2.5 text-sm text-slate-700">
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#9cbb5a]" aria-hidden />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-slate-300" aria-hidden />
                  )}
                  <span className={done ? "text-slate-800" : "text-slate-500"}>{item.label}</span>
                </li>
              );
            })}
          </ul>
          <p className="text-sm text-slate-500">Team Handoff™ · Team Takeover™</p>
        </section>

        <RecommendedEmployeeCard
          state={effectiveState}
          displayName={displayName}
          displayRole={displayRole}
          avatarUrl={avatarUrl}
        />
      </div>
    </div>
  );
}
